import Modal from "components/DesignSystem/Modal/Modal";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { Stepper } from "components/DesignSystem/Stepper/Stepper";
import { ArrowRight } from "components/icons/ArrowRight";
import { PhysicalNexusTrackerModalBody } from "./PhysicalNexusTrackerModalBody";
import { Form, Formik, FormikValues } from "formik";
import {
  PhysicalNexusTracker,
  useGetPhysicalNexusTrackerQuery,
  useUpdatePhysicalNexusTrackerMutation,
} from "store/apis/salesTax";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { BackendError } from "types/utils/error";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { boolToString } from "./SalesNexusTracker";
import { US } from "constants/countryCodes";
import { Button } from "components/DesignSystem/Button/Button";
import { checkPhysicalNexusValidation } from "formValidations/checkPhysicalNexus";

export type PhysicalNexusTrackerFormValues = {
  office_present: string;
  office_in_us: string[];
  warehouse_present: string;
  warehouse_in_us: string[];
  employees_present: string;
  employees_in_us: string[];
};

export const YES = "yes";

const PhysicalNexusTrackerStepper = ({
  currentStep,
  onStepperClick,
}: {
  onStepperClick: (step: number) => void;
  currentStep: number;
}) => {
  const breadcrumbs = [
    { name: "Office", step: 1 },
    { name: "Warehouse", step: 2 },
    { name: "Employee", step: 3 },
  ];

  return (
    <div className="t-pt-2">
      <Stepper size="small" direction="horizontal">
        {breadcrumbs?.map(({ name, step }) => (
          <Stepper.Step
            step={step}
            key={step}
            isActive={currentStep >= step}
            clickable={currentStep >= step}
            onClick={() => onStepperClick(step)}
          >
            <div className="t-flex t-items-center">
              {name}
              {step !== breadcrumbs.length && (
                <ArrowRight color="currentColor" />
              )}
            </div>
          </Stepper.Step>
        ))}
      </Stepper>
    </div>
  );
};

export const PhysicalNexusTrackerModal = ({
  onClose,
  isOpen,
  hasAlreadyCheckedNexus,
}: {
  isOpen: boolean;
  onClose: () => void;
  hasAlreadyCheckedNexus: boolean;
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const { alertToast, successToast } = useToast();
  const entityId = useCurrentEntityId();
  const {
    data: physicalNexusTrackerData,
    isLoading,
    isSuccess,
  } = useGetPhysicalNexusTrackerQuery(
    {
      entityId,
    },
    {
      skip: !entityId || !isOpen,
    }
  );
  const [
    updatePhysicalNexusTrackerData,
    { isLoading: isUpdatingPhysicalNexus },
  ] = useUpdatePhysicalNexusTrackerMutation();

  const handleSubmit = async (values: FormikValues) => {
    if (
      currentStep === 1 &&
      Boolean(
        (values.office_present === "yes" && values.office_in_us?.length) ||
          values.office_present !== "yes"
      )
    ) {
      setCurrentStep((prev) => prev + 1);
      return;
    }

    if (
      currentStep === 2 &&
      Boolean(
        (values.warehouse_present === "yes" &&
          values.warehouse_in_us?.length) ||
          values.warehouse_present !== "yes"
      )
    ) {
      setCurrentStep((prev) => prev + 1);
      return;
    }

    if (
      currentStep === 3 &&
      Boolean(
        (values.employees_present === "yes" &&
          values.employees_in_us?.length) ||
          values.employees_present !== "yes"
      )
    ) {
      const payload = {
        ...values,
        office_present: values.office_present
          ? values.office_present === YES
          : null,
        warehouse_present: values.warehouse_present
          ? values.warehouse_present === YES
          : null,
        employees_present: values.employees_present
          ? values.employees_present === YES
          : null,
      };

      try {
        await updatePhysicalNexusTrackerData({
          entityId,
          payload: payload as PhysicalNexusTracker,
        }).unwrap();
        successToast({
          message: "Physical nexus details updated",
        });
        setCurrentStep(1);
        onClose();
      } catch (error) {
        alertToast({ message: (error as BackendError).data?.error?.message });
      }
    }
  };

  const { data: countries, isLoading: isCountryLoading } =
    useGetCountriesDataQuery();

  const unitatedStates = countries?.find(
    (country) => country.code_alpha_2 === US
  )?.states;

  const stateOptions = unitatedStates?.map((state) => ({
    label: state.name,
    value: state.uuid,
  }));

  return (
    <>
      <Modal.Root
        open={isOpen}
        onOpenChange={() => {
          setCurrentStep(1);
          onClose();
        }}
      >
        <Modal.Content size="regular" useCustomOverlay>
          <Modal.Header>
            <div>
              <Modal.Title>
                {hasAlreadyCheckedNexus
                  ? "Update Physical Nexus"
                  : "Check Physical Nexus"}
              </Modal.Title>

              <Modal.Subtitle>
                <PhysicalNexusTrackerStepper
                  currentStep={currentStep}
                  onStepperClick={setCurrentStep}
                />
              </Modal.Subtitle>
            </div>
            <Modal.Close />
          </Modal.Header>
          <Async.Root
            isLoading={isLoading || isCountryLoading}
            isSuccess={isSuccess}
            isEmpty={false}
          >
            <Async.Success>
              <Formik
                onSubmit={handleSubmit}
                enableReinitialize
                initialValues={{
                  ...physicalNexusTrackerData,
                  office_present:
                    boolToString[
                      String(physicalNexusTrackerData?.office_present)
                    ] || "yes",
                  warehouse_present:
                    boolToString[
                      String(physicalNexusTrackerData?.warehouse_present)
                    ] || "yes",
                  employees_present:
                    boolToString[
                      String(physicalNexusTrackerData?.employees_present)
                    ] || "yes",
                }}
              >
                <Form>
                  <PhysicalNexusTrackerModalBody
                    currentStep={currentStep}
                    stateOptions={stateOptions}
                  />

                  <Modal.FooterButtonGroup>
                    <Button
                      customType="secondary"
                      onClick={() => {
                        setCurrentStep(1);
                        onClose();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      customType="primary"
                      isLoading={isUpdatingPhysicalNexus}
                    >
                      {currentStep === 3 ? "Save" : "Next"}
                    </Button>
                  </Modal.FooterButtonGroup>
                </Form>
              </Formik>
            </Async.Success>
          </Async.Root>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
